<template>
  <div id="app">
    <v-pull-hook />

    <transition name="fade" mode="out-in">
      <keep-alive :max="6" :exclude="['MessageThreadShow']">
        <router-view :key="$route.path">
          <div id="overlay-portal"></div>
          <div id="cta-row-portal"></div>
          <div id="modal-portal"></div>
        </router-view>
      </keep-alive>
    </transition>

    <auth-modal />
    <form-modal />
    <checkout-modal-trigger v-if="!isCheckoutPage" />
    <scheduled-content-modal-trigger />
    <overlay-modal />
    <navigation-page-modal />
    <app-download-bar />
    <tabbar />
    <block-action-trigger />
    <notification-permission-trigger />
    <global-loader-trigger />
    <router-queue-trigger />
    <global-audio-player />
    <view-switcher />
    <toast :visible="!!toastMessage" action-text="OK" @action="closeToast">
      <span>{{ toastMessage }}</span>
    </toast>
  </div>
</template>

<script>
import { setOptions, bootstrap } from "vue-gtag"

import { mapState, mapActions, mapGetters, mapMutations } from "vuex"

import { NAVIGATION_EVENTS } from "@/plugins/router/shared"

import pwaMeta from "@/lib/pwa-meta"
import appHeightWatcher from "@/lib/app-height-watcher"
import isAppStore from "@/lib/is-platform/is-app-store"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { isCheckoutPage } from "@/lib/checkout-utils"
import { HIDE_QUERY_PARAM, MANAGED_PAGE_PREFIX } from "@/lib/constants"
import { MVT_EVENTS } from "@/lib/globals"

export default {
  components: {
    AuthModal: () =>
      import(/* webpackChuckName: "AuthModal" */ "@/components/AuthModal"),
    Tabbar: () =>
      import(/* webpackChuckName: "Tabbar" */ "@/components/Tabbar"),
    FormModal: () => import("@/components/modals/FormModal"),
    NavigationPageModal: () =>
      import("@/components/modals/NavigationPageModal"),
    AppDownloadBar: () => import("@/components/AppDownloadBar"),
    CheckoutModalTrigger: () =>
      import("@/components/checkout/CheckoutModalTrigger"),
    ScheduledContentModalTrigger: () =>
      import("@/components/modals/ScheduledContentModalTrigger"),
    OverlayModal: () => import("@/components/modals/OverlayModal"),
    BlockActionTrigger: () =>
      import("@/components/block-actions/BlockActionTrigger"),
    NotificationPermissionTrigger: () =>
      import("@/components/modals/NotificationPermissionTrigger"),
    GlobalLoaderTrigger: () => import("@/components/GlobalLoaderTrigger"),
    RouterQueueTrigger: () => import("@/components/RouterQueueTrigger"),
    GlobalAudioPlayer: () => import("@/components/audio/GlobalPlayer"),
    ViewSwitcher: () => import("@/components/ViewSwitcher"),
    Toast: () => import("@/components/Toast.vue")
  },

  data() {
    return {
      isInitialized: false,
      toastMessage: null,
      toastTimeout: null
    }
  },

  head() {
    return {
      ...pwaMeta(this.global || {})
    }
  },

  computed: {
    ...mapState(["global"]),
    ...mapGetters(["app"]),
    ...mapGetters("router", ["historyType"]),
    ...mapGetters("auth", ["currentUser", "isAuthenticated"]),
    ...mapGetters("localisation", ["getLocalisation", "currentLocale"]),

    isCheckoutPage() {
      return isCheckoutPage(this.$route.path)
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function() {
        if (this.isAuthenticated) {
          this.setupGlobalChanel()
        }
      }
    },

    currentLocale: {
      immediate: true,

      handler: function() {
        const localisation = this.getLocalisation(this.currentLocale)

        if (localisation && localisation.text_direction === "rtl") {
          document.body.setAttribute("dir", "rtl")
        } else {
          document.body.removeAttribute("dir")
        }
      }
    }
  },

  methods: {
    ...mapMutations("ui", ["setHiddenElements"]),

    ...mapActions("auth", { initializeAuth: "initialize" }),
    ...mapActions("pwa", ["setupInstallPrompt"]),
    ...mapActions("messages", ["initializeGlobalChannel"]),
    ...mapActions("analytics", [
      "trackActivity",
      "trackPagePush",
      "trackPagePop"
    ]),

    setupGlobalChanel() {
      if (this.isAuthenticated) {
        this.initializeGlobalChannel(this.currentUser?.globalEventsChannel)
      }
    },

    enableGoogleAnalytics(id) {
      setOptions({
        includes: id ? [{ id }] : [],
        config: { id: process.env.VUE_APP_GOOGLE_ANALYTICS_ID }
      })

      bootstrap()
    },

    handleCheckoutCompleted() {
      try {
        if (window.rewardful) {
          window.rewardful("convert", { email: this.currentUser?.email })
        }
      } catch (e) {
        console.error(e)
      }
    },

    handlePushToastNotify(e) {
      clearTimeout(this.toastTimeout)

      this.toastMessage = e.message

      this.toastTimeout = setTimeout(() => {
        this.toastMessage = null
      }, 5000)
    },

    closeToast() {
      clearTimeout(this.toastTimeout)
      this.toastMessage = null
    },

    persistHiddenElements() {
      this.$router.onReady(() => {
        const hiddenElements = (this.$route.query[HIDE_QUERY_PARAM] || "")
          .split(",")
          .filter(Boolean)

        return this.setHiddenElements(hiddenElements)
      })
    }
  },

  mounted() {
    this.initializeAuth()
    this.setupInstallPrompt()
    this.persistHiddenElements()
    this.$router.on(NAVIGATION_EVENTS.PUSH, this.trackPagePush)
    this.$router.on(NAVIGATION_EVENTS.POP, this.trackPagePop)
    appHeightWatcher()

    EventBus.$on(EVENTS.CHECKOUT_COMPLETED, this.handleCheckoutCompleted)
    EventBus.$on(EVENTS.PUSH_TOAST_NOTIFY, this.handlePushToastNotify)

    this.enableGoogleAnalytics(this.app?.google_analytics_id)

    this.$nextTick(() => {
      this.isInitialized = true

      if (isAppStore()) {
        const { initialize } = require("@/lib/app-store-utils")
        initialize(this)
      }

      const dispatchEvent = () =>
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent(MVT_EVENTS.ON_PAGE_INITIAL_LOAD))
        }, 1000)

      if (this.$route.path.startsWith(MANAGED_PAGE_PREFIX)) {
        dispatchEvent()
      } else {
        EventBus.$once(EVENTS.PAGE_BLOCKS_RENDERED, dispatchEvent)
      }

      const { default: MovementSdk } = require("@/sdk/movement-sdk")
      const { setupGTMIntegration } = require("@/lib/gtm-utils")

      // Initialize the Movement SDK & GTM Integration
      window.$mvt = new MovementSdk()
      const eventNames = Object.values(window.$mvt.MVT_EVENTS || {})
      setupGTMIntegration({
        eventManager: window.$mvt.events,
        events: eventNames
      })
    })
  }
}
</script>

<style lang="scss">
:root {
  --safe-area-inset-top: env(safe-area-inset-top);
  --container-width: 706px;
}

.debug-bar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000;
  background: black;
  color: white;
}
</style>
